/**
 * Centralized data-fetching layer (TanStack Query).
 *
 * Standardizes server-state across the app: caching, automatic refetch,
 * loading/error states, optimistic invalidation after mutations, and smooth
 * pagination via `placeholderData`. Pages call these hooks instead of doing
 * ad-hoc `api.get().then(setState)`.
 */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api } from "@/lib/api";

const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v != null));
const get = (url, params) => api.get(url, { params }).then((r) => r.data);

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
export function useDashboard() {
  return useQuery({ queryKey: ["dashboard"], queryFn: () => get("/dashboard") });
}

// Lightweight action counts for sidebar nav badges (refetched on focus + interval).
export function useNavBadges() {
  return useQuery({
    queryKey: ["nav-badges"],
    queryFn: () => get("/nav/badges"),
    staleTime: 30000,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });
}

export function useReferenceMeta() {
  return useQuery({ queryKey: ["reference-meta"], queryFn: () => get("/reference/meta"), staleTime: 5 * 60_000 });
}

// --- Threat & Regulatory Radar (firm-only) ---
export function useRadarSignals({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["radar-signals", page, pageSize, filters],
    queryFn: () => get("/radar/signals", { page, page_size: pageSize, ...clean(filters || {}) }),
    placeholderData: keepPreviousData,
  });
}

export function useRadarStats() {
  return useQuery({ queryKey: ["radar-stats"], queryFn: () => get("/radar/stats"), staleTime: 60_000 });
}

export function useRadarSignalImpact(id, enabled = true) {
  return useQuery({
    queryKey: ["radar-impact", id],
    queryFn: () => get(`/radar/signals/${id}/impacted`),
    enabled: !!id && enabled,
  });
}

export function useWorkspaceRadar(wid, filters) {
  return useQuery({
    queryKey: ["workspace-radar", wid, filters],
    queryFn: () => get(`/workspaces/${wid}/radar`, clean(filters || {})),
    enabled: !!wid,
  });
}

export function useDraftAdvisory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signalId, workspaceId }) =>
      api.post(`/radar/signals/${signalId}/advisory`, { workspace_id: workspaceId }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outputs"] });
      qc.invalidateQueries({ queryKey: ["nav-badges"] });
    },
  });
}

// ---- Ask DAAKYI — Firm Brain --------------------------------------------
export function useFirmBrainSuggestions() {
  return useQuery({
    queryKey: ["firm-brain-suggestions"],
    queryFn: () => get("/firm-brain/suggestions"),
    staleTime: 10 * 60_000,
  });
}

export function useFirmBrainChat() {
  return useMutation({
    mutationFn: ({ question, history }) =>
      api.post("/firm-brain/chat", { question, history }).then((r) => r.data),
  });
}

// ---- Kasara — platform copilot ------------------------------------------
export function useKasaraSuggestions(workspaceId) {
  return useQuery({
    queryKey: ["kasara-suggestions", workspaceId || "global"],
    queryFn: () => get("/kasara/suggestions", workspaceId ? { workspace_id: workspaceId } : undefined),
    staleTime: 5 * 60_000,
  });
}

export function useKasaraChat() {
  return useMutation({
    mutationFn: ({ message, conversationId, context }) =>
      api.post("/kasara/chat", { message, conversation_id: conversationId, context }).then((r) => r.data),
  });
}

export function useKasaraConversations() {
  return useQuery({
    queryKey: ["kasara-conversations"],
    queryFn: () => get("/kasara/conversations"),
    staleTime: 30_000,
  });
}

export function useKasaraBriefing() {
  return useQuery({
    queryKey: ["kasara-briefing"],
    queryFn: () => get("/kasara/briefing"),
    staleTime: 5 * 60_000,
  });
}

export function useOutputs({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["outputs", page, pageSize, filters],
    queryFn: () => get("/outputs", { page, page_size: pageSize, ...clean(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useOutput(id, enabled = true) {
  return useQuery({
    queryKey: ["output", id],
    queryFn: () => get(`/outputs/${id}`),
    enabled: !!id && enabled,
  });
}

// ---------------------------------------------------------------------------
// Reuse Engine (Phase 13 — "Prior Art" recommender, L3 network effects)
// ---------------------------------------------------------------------------
export function useReuseRecommend({ outputId, query, type, workspaceId, limit = 6, enabled = true } = {}) {
  return useQuery({
    queryKey: ["reuse-recommend", outputId || null, query || "", type || "", workspaceId || null, limit],
    queryFn: () => api.post("/reuse/recommend", {
      output_id: outputId || undefined, query: query || undefined,
      type: type || undefined, workspace_id: workspaceId || undefined, limit,
    }).then((r) => r.data),
    enabled,
    staleTime: 60_000,
  });
}

export function useReuseAdapt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/reuse/adapt", body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outputs"] });
      qc.invalidateQueries({ queryKey: ["nav-badges"] });
      qc.invalidateQueries({ queryKey: ["reuse-analytics"] });
    },
  });
}

export function useReuseAnalytics(enabled = true) {
  return useQuery({
    queryKey: ["reuse-analytics"],
    queryFn: () => get("/reuse/analytics"),
    enabled,
    staleTime: 60_000,
  });
}

export function useFirmDNA(enabled = true) {
  return useQuery({
    queryKey: ["firm-dna-health"],
    queryFn: () => get("/firm-dna/health"),
    enabled,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Outcome & Learning Loop (Phase 14 — L4 moat)
// ---------------------------------------------------------------------------
export function useRecordOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/outcomes", body).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["output", vars.output_id] });
      qc.invalidateQueries({ queryKey: ["outcome-track-record", vars.output_id] });
      qc.invalidateQueries({ queryKey: ["outcome-analytics"] });
      qc.invalidateQueries({ queryKey: ["reuse-analytics"] });
    },
  });
}

export function useOutcomeTrackRecord(outputId, enabled = true) {
  return useQuery({
    queryKey: ["outcome-track-record", outputId],
    queryFn: () => get(`/outcomes/output/${outputId}`),
    enabled: !!outputId && enabled,
    staleTime: 30_000,
  });
}

export function useOutcomeAnalytics(enabled = true) {
  return useQuery({
    queryKey: ["outcome-analytics"],
    queryFn: () => get("/outcomes/analytics"),
    enabled,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Continuous Assurance loop (Phase 15 — L5)
// ---------------------------------------------------------------------------
export function usePublishToClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ outputId, override = false, overrideReason = "" }) =>
      api.post(`/outputs/${outputId}/publish-to-client`, { override, override_reason: overrideReason }).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["output", vars.outputId] });
      qc.invalidateQueries({ queryKey: ["assurance-loop"] });
      qc.invalidateQueries({ queryKey: ["loop-overview"] });
    },
  });
}

export function useUnpublishOutput() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ outputId }) => api.post(`/outputs/${outputId}/unpublish`).then((r) => r.data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["output", vars.outputId] }),
  });
}

export function useAssuranceLoop(wid, enabled = true) {
  return useQuery({
    queryKey: ["assurance-loop", wid],
    queryFn: () => get(`/workspaces/${wid}/assurance-loop`),
    enabled: !!wid && enabled,
    staleTime: 30_000,
  });
}

export function useLoopOverview(enabled = true) {
  return useQuery({
    queryKey: ["loop-overview"],
    queryFn: () => get("/assurance/loop-overview"),
    enabled,
    staleTime: 30_000,
  });
}

// Regulatory change → advisory loop (reuses the Radar advisory + Phase-15 loop).
export function useRegulationImpacted(regId, enabled = true) {
  return useQuery({
    queryKey: ["regulation-impacted", regId],
    queryFn: () => get(`/regulations/${regId}/impacted`),
    enabled: !!regId && enabled,
    staleTime: 60_000,
  });
}

export function useDraftRegulationAdvisory() {
  return useMutation({
    mutationFn: ({ regId, workspace_id, change_note }) =>
      api.post(`/regulations/${regId}/advisory`, { workspace_id, change_note }).then((r) => r.data),
  });
}


export function usePortalAdvisories(wid, enabled = true) {
  return useQuery({
    queryKey: ["portal-advisories", wid],
    queryFn: () => get(`/workspaces/${wid}/portal/advisories`),
    enabled: !!wid && enabled,
    staleTime: 30_000,
  });
}

export function useKnowledgeDocuments({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["knowledge", page, pageSize, filters],
    queryFn: () => get("/knowledge/documents", { page, page_size: pageSize, ...clean(filters) }),
    placeholderData: keepPreviousData,
  });
}

// Approved, indexed documents available as AI grounding context (for the picker).
export function useApprovedKnowledge() {
  return useQuery({
    queryKey: ["knowledge-approved"],
    queryFn: () => get("/knowledge/documents", { page: 1, page_size: 100, status: "Approved" }),
    staleTime: 60_000,
  });
}

export function useAuditLogs({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["audit", page, pageSize, filters],
    queryFn: () => get("/audit/logs", { page, page_size: pageSize, ...clean(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useAuditActions() {
  return useQuery({ queryKey: ["audit-actions"], queryFn: () => get("/audit/actions"), staleTime: 5 * 60_000 });
}

export function useUsers({ page, pageSize }) {
  return useQuery({
    queryKey: ["users", page, pageSize],
    queryFn: () => get("/users", { page, page_size: pageSize }),
    placeholderData: keepPreviousData,
  });
}

export function useReviewQueue({ page, pageSize }) {
  return useQuery({
    queryKey: ["review-queue", page, pageSize],
    queryFn: () => get("/outputs/queue", { page, page_size: pageSize }),
    placeholderData: keepPreviousData,
  });
}

// Lightweight count for the top-bar review badge (polls periodically).
export function useReviewQueueCount(enabled) {
  return useQuery({
    queryKey: ["review-queue-count"],
    queryFn: () => get("/outputs/queue", { page: 1, page_size: 1 }).then((d) => d.total),
    enabled: !!enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------
export function useBulkReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, review_status, note }) => api.post("/outputs/bulk-review", { ids, review_status, note }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outputs"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["audit"] });
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      qc.invalidateQueries({ queryKey: ["review-queue-count"] });
      qc.invalidateQueries({ queryKey: ["nav-badges"] });
    },
  });
}

export function useRadarRefresh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/radar/refresh").then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["radar-signals"] });
      qc.invalidateQueries({ queryKey: ["radar-stats"] });
    },
  });
}

export function useReviewOutput() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, review_status }) => api.post(`/outputs/${id}/review`, { review_status }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["outputs"] });
      qc.invalidateQueries({ queryKey: ["output", vars.id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["audit"] }); // review emits an audit row
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      qc.invalidateQueries({ queryKey: ["review-queue-count"] });
    },
  });
}

export function useEditOutput() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content, title, reason }) => api.patch(`/outputs/${id}`, { content, title, reason }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["outputs"] });
      qc.invalidateQueries({ queryKey: ["output-versions", vars.id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["audit"] });
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      qc.invalidateQueries({ queryKey: ["review-queue-count"] });
    },
  });
}

export function useOutputVersions(id, enabled) {
  return useQuery({
    queryKey: ["output-versions", id],
    queryFn: () => get(`/outputs/${id}/versions`),
    enabled: !!id && !!enabled,
  });
}

export function useRestoreVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }) => api.post(`/outputs/${id}/restore/${version}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["outputs"] });
      qc.invalidateQueries({ queryKey: ["output", vars.id] });
      qc.invalidateQueries({ queryKey: ["output-versions", vars.id] });
      qc.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useWorkspaces({ page = 1, pageSize = 50, filters } = {}) {
  return useQuery({
    queryKey: ["workspaces", page, pageSize, filters],
    queryFn: () => get("/workspaces", { page, page_size: pageSize, ...clean(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useWorkspace(id) {
  return useQuery({
    queryKey: ["workspace", id],
    queryFn: () => get(`/workspaces/${id}`),
    enabled: !!id,
  });
}

// Golden Path — the 7-stage engagement journey read model (firm) + client mirror (C1–C5).
export function useGoldenPath(wid) {
  return useQuery({ queryKey: ["golden-path", wid], queryFn: () => get(`/workspaces/${wid}/golden-path`), enabled: !!wid, staleTime: 30_000 });
}
export function useClientGoldenPath(wid) {
  return useQuery({ queryKey: ["golden-path-client", wid], queryFn: () => get(`/workspaces/${wid}/golden-path/client`), enabled: !!wid, staleTime: 30_000 });
}
export function useCommandCenterGoldenPath() {
  return useQuery({ queryKey: ["cc-golden-path"], queryFn: () => get("/command-center/golden-path"), staleTime: 60_000 });
}

// {workspace_id: slug} map (RBAC-scoped) so firm surfaces can build readable /clients/<slug> links.
export function useWorkspaceSlugIndex() {
  return useQuery({
    queryKey: ["workspace-slug-index"],
    queryFn: () => get("/workspaces/slug-index"),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/workspaces", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });
}

export function useUpdateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/workspaces/${id}`, body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      qc.invalidateQueries({ queryKey: ["workspace", vars.id] });
    },
  });
}

export function useWorkspaceBoard() {
  return useQuery({ queryKey: ["workspace-board"], queryFn: () => get("/workspaces/board") });
}

export function useMoveWorkspaceStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dimension, value }) => api.patch(`/workspaces/${id}/stage`, { dimension, value }),
    // Optimistic: move the card immediately, roll back on error.
    onMutate: async ({ id, dimension, value }) => {
      await qc.cancelQueries({ queryKey: ["workspace-board"] });
      const prev = qc.getQueryData(["workspace-board"]);
      const field = dimension === "relationship" ? "relationship_status" : "opportunity_status";
      if (prev?.items) {
        qc.setQueryData(["workspace-board"], {
          ...prev,
          items: prev.items.map((w) => (w.id === id ? { ...w, [field]: value } : w)),
        });
      }
      return { prev };
    },
    onError: (_e, _vars, ctx) => { if (ctx?.prev) qc.setQueryData(["workspace-board"], ctx.prev); },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["workspace-board"] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

export function useArchiveWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/workspaces/${id}/archive`),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      qc.invalidateQueries({ queryKey: ["workspace", id] });
    },
  });
}

export function useBulkArchiveWorkspaces() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids) => api.post(`/workspaces/bulk-archive`, { ids }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });
}

// Generic per-user saved views (filter presets) for list pages.
export function useSavedViews(page) {
  return useQuery({
    queryKey: ["saved-views", page],
    queryFn: () => get(`/saved-views?page=${encodeURIComponent(page)}`).then((d) => d.items || []),
    enabled: !!page,
    staleTime: 60_000,
  });
}

export function useCreateSavedView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ page, name, view }) => api.post(`/saved-views`, { page, name, view }).then((r) => r.data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["saved-views", vars.page] }),
  });
}

export function useDeleteSavedView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => api.delete(`/saved-views/${id}`),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["saved-views", vars.page] }),
  });
}


export function useWorkspaceMeta() {
  return useQuery({
    queryKey: ["workspace-meta"],
    queryFn: () => get("/workspaces/meta"),
    staleTime: 1000 * 60 * 60,
  });
}

export function useUploadWorkspaceLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post(`/workspaces/${id}/logo`, fd).then((r) => r.data);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["workspace", vars.id] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

export function useRemoveWorkspaceLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/workspaces/${id}/logo`).then((r) => r.data),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ["workspace", id] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

export function useAddWorkspaceEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.post(`/workspaces/${id}/events`, body),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["workspace", vars.id] }),
  });
}

export function useDeleteWorkspaceEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, eventId }) => api.delete(`/workspaces/${id}/events/${eventId}`),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["workspace", vars.id] }),
  });
}

// --- External Intelligence Agent (workspace enrichment) ----------------------
export function useWorkspaceEnrichment(id) {
  return useQuery({
    queryKey: ["enrichment", id],
    queryFn: () => get(`/workspaces/${id}/enrichment`),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.latest_scan?.status === "running" ? 4000 : false),
  });
}

export function useEnrichWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, website, notes }) => api.post(`/workspaces/${id}/enrich`, { website, notes }),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["enrichment", vars.id] }),
  });
}

export function useReviewFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ findingId, review_status, approved_value, notes }) =>
      api.patch(`/enrichment/findings/${findingId}`, { review_status, approved_value, notes }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["enrichment", vars.workspaceId] });
      qc.invalidateQueries({ queryKey: ["workspace", vars.workspaceId] });
    },
  });
}



export function useAssignOutputWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, workspace_id }) => api.post(`/outputs/${id}/assign-workspace`, { workspace_id }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["outputs"] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      if (vars.workspace_id) qc.invalidateQueries({ queryKey: ["workspace", vars.workspace_id] });
    },
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: () => get("/notifications", { limit: 20 }),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useSecurityOverview() {
  return useQuery({
    queryKey: ["security-overview"],
    queryFn: () => get("/security/overview"),
  });
}

export function useGlobalSearch(q) {
  return useQuery({
    queryKey: ["global-search", q],
    queryFn: () => get("/search", { q }),
    enabled: !!q && q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/users/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useResetMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/auth/mfa/admin/${id}/reset`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useUpdateDocumentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }) => api.patch(`/knowledge/documents/${id}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["knowledge"] });
      qc.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

// --- P1: System health, error tracking & LLM budget ---
export function useSystemHealth() {
  return useQuery({
    queryKey: ["system-health"],
    queryFn: () => get("/system/health"),
    refetchInterval: 30_000,
  });
}

export function useSystemErrors(resolved) {
  return useQuery({
    queryKey: ["system-errors", resolved],
    queryFn: () => get("/system/errors", clean({ limit: 100, resolved })),
    placeholderData: keepPreviousData,
  });
}

export function useResolveError() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/system/errors/${id}/resolve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system-errors"] });
      qc.invalidateQueries({ queryKey: ["system-health"] });
    },
  });
}

// --- Administration console ---
export function useSystemEnvironment() {
  return useQuery({ queryKey: ["system-environment"], queryFn: () => get("/system/environment"), staleTime: 60_000 });
}

export function useDataOverview() {
  return useQuery({ queryKey: ["data-overview"], queryFn: () => get("/system/data-overview"), staleTime: 60_000 });
}

export function useBudget() {
  return useQuery({ queryKey: ["budget"], queryFn: () => get("/analytics/budget") });
}

export function useUpdateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.put("/analytics/budget", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["budget"] });
      qc.invalidateQueries({ queryKey: ["system-health"] });
    },
  });
}

export function useCostCaps() {
  return useQuery({ queryKey: ["cost-caps"], queryFn: () => get("/analytics/cost-caps") });
}

export function useUpdateCostCap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scope, key, limit_usd }) => api.put("/analytics/cost-caps", { scope, key, limit_usd }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cost-caps"] }),
  });
}


// --- Global Intelligence (global organization directory) ---------------------
export function useIntelMeta() {
  return useQuery({ queryKey: ["intel-meta"], queryFn: () => get("/intelligence/meta"), staleTime: 5 * 60_000 });
}

export function useIntelClients({ country, page, pageSize, filters, sort } = {}) {
  return useQuery({
    queryKey: ["intel-clients", country, page, pageSize, filters, sort],
    queryFn: () => get("/intelligence/clients", {
      ...(country ? { country } : {}), page, page_size: pageSize, sort, ...clean(filters || {}),
    }),
    placeholderData: keepPreviousData,
  });
}

export function useIntelDashboard() {
  return useQuery({ queryKey: ["intel-dashboard"], queryFn: () => get("/intelligence/dashboard"), staleTime: 60_000 });
}

export function useIntelClient(id) {
  return useQuery({ queryKey: ["intel-client", id], queryFn: () => get(`/intelligence/clients/${id}`), enabled: !!id });
}

export function parseIntelQuery(query) {
  return get("/intelligence/search/parse", { query });
}

export function useIntelStats(country) {
  return useQuery({ queryKey: ["intel-stats", country], queryFn: () => get("/intelligence/clients/stats", { country }) });
}

function useIntelInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["intel-clients"] });
    qc.invalidateQueries({ queryKey: ["intel-client"] });
    qc.invalidateQueries({ queryKey: ["intel-stats"] });
    qc.invalidateQueries({ queryKey: ["intel-dashboard"] });
  };
}

export function useCreateIntelClient() {
  const invalidate = useIntelInvalidate();
  return useMutation({ mutationFn: (body) => api.post("/intelligence/clients", body), onSuccess: invalidate });
}

export function useUpdateIntelClient() {
  const invalidate = useIntelInvalidate();
  return useMutation({ mutationFn: ({ id, ...body }) => api.patch(`/intelligence/clients/${id}`, body), onSuccess: invalidate });
}

export function useUploadIntelLogo() {
  const invalidate = useIntelInvalidate();
  return useMutation({
    mutationFn: ({ id, file }) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post(`/intelligence/clients/${id}/logo`, fd).then((r) => r.data);
    },
    onSuccess: invalidate,
  });
}

export function useRemoveIntelLogo() {
  const invalidate = useIntelInvalidate();
  return useMutation({ mutationFn: (id) => api.delete(`/intelligence/clients/${id}/logo`).then((r) => r.data), onSuccess: invalidate });
}

export function useDeleteIntelClient() {
  const invalidate = useIntelInvalidate();
  return useMutation({ mutationFn: (id) => api.delete(`/intelligence/clients/${id}`), onSuccess: invalidate });
}

export function useImportIntelClients() {
  const invalidate = useIntelInvalidate();
  return useMutation({ mutationFn: (body) => api.post("/intelligence/clients/import", body), onSuccess: invalidate });
}

export function useConvertIntelClient() {
  const invalidate = useIntelInvalidate();
  return useMutation({
    mutationFn: ({ id, relationship_status, engagement_type, apply_research }) =>
      api.post(`/intelligence/clients/${id}/convert`, { relationship_status, engagement_type, apply_research }),
    onSuccess: invalidate,
  });
}

export function useEnrichIntelClient() {
  const invalidate = useIntelInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/intelligence/clients/${id}/enrich`),
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ["workspaces"] }); },
  });
}

export function useGenerateResearch() {
  const invalidate = useIntelInvalidate();
  return useMutation({
    mutationFn: (id) => api.post(`/intelligence/clients/${id}/research`).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useApproveResearch() {
  const invalidate = useIntelInvalidate();
  return useMutation({
    mutationFn: (id) => api.post(`/intelligence/clients/${id}/research/approve`).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useDiscardResearch() {
  const invalidate = useIntelInvalidate();
  return useMutation({
    mutationFn: (id) => api.post(`/intelligence/clients/${id}/research/discard`).then((r) => r.data),
    onSuccess: invalidate,
  });
}

// --- Watchlists & saved searches (Phase 3) ---
function useWatchlistInvalidate() {
  const qc = useQueryClient();
  const invalidateIntel = useIntelInvalidate();
  return () => {
    qc.invalidateQueries({ queryKey: ["watchlists"] });
    qc.invalidateQueries({ queryKey: ["watchlist"] });
    invalidateIntel();
  };
}

export function useWatchlists() {
  return useQuery({ queryKey: ["watchlists"], queryFn: () => get("/intelligence/watchlists") });
}
export function useWatchlist(id) {
  return useQuery({ queryKey: ["watchlist", id], queryFn: () => get(`/intelligence/watchlists/${id}`), enabled: !!id });
}
export function useCreateWatchlist() {
  const invalidate = useWatchlistInvalidate();
  return useMutation({ mutationFn: (body) => api.post("/intelligence/watchlists", body).then((r) => r.data), onSuccess: invalidate });
}
export function useUpdateWatchlist() {
  const invalidate = useWatchlistInvalidate();
  return useMutation({ mutationFn: ({ id, ...body }) => api.patch(`/intelligence/watchlists/${id}`, body).then((r) => r.data), onSuccess: invalidate });
}
export function useDeleteWatchlist() {
  const invalidate = useWatchlistInvalidate();
  return useMutation({ mutationFn: (id) => api.delete(`/intelligence/watchlists/${id}`), onSuccess: invalidate });
}
export function useAddWatchlistMember() {
  const invalidate = useWatchlistInvalidate();
  return useMutation({ mutationFn: ({ id, ...body }) => api.post(`/intelligence/watchlists/${id}/members`, body).then((r) => r.data), onSuccess: invalidate });
}
export function useRemoveWatchlistMember() {
  const invalidate = useWatchlistInvalidate();
  return useMutation({ mutationFn: ({ id, memberId }) => api.delete(`/intelligence/watchlists/${id}/members/${memberId}`), onSuccess: invalidate });
}

export function useSavedSearches() {
  return useQuery({ queryKey: ["intel-saved-searches"], queryFn: () => get("/intelligence/saved-searches") });
}
export function useCreateSavedSearch() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body) => api.post("/intelligence/saved-searches", body).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["intel-saved-searches"] }) });
}
export function useDeleteSavedSearch() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id) => api.delete(`/intelligence/saved-searches/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["intel-saved-searches"] }) });
}

export function intelDuplicateCheck(id) {
  return get(`/intelligence/clients/${id}/duplicate-check`);
}

// --- Scoping questionnaires (Phase 5) ---
export function useScopingMeta() {
  return useQuery({ queryKey: ["scoping-meta"], queryFn: () => get("/scoping/meta"), staleTime: 5 * 60_000 });
}
export function useWorkspaceScoping(wid) {
  return useQuery({ queryKey: ["scoping-ws", wid], queryFn: () => get(`/scoping/workspaces/${wid}`), enabled: !!wid });
}
function useScopingInvalidate() {
  const qc = useQueryClient();
  return (wid) => {
    qc.invalidateQueries({ queryKey: ["scoping-ws", wid] });
    qc.invalidateQueries({ queryKey: ["scoping"] });
    if (wid) qc.invalidateQueries({ queryKey: ["workspace", wid] });
  };
}
export function useCreateScoping() {
  const invalidate = useScopingInvalidate();
  return useMutation({
    mutationFn: ({ wid, ...body }) => api.post(`/scoping/workspaces/${wid}`, body).then((r) => r.data),
    onSuccess: (_d, v) => invalidate(v.wid),
  });
}
export function useUpdateScoping() {
  const invalidate = useScopingInvalidate();
  return useMutation({
    mutationFn: ({ sid, ...body }) => api.patch(`/scoping/${sid}`, body).then((r) => r.data),
    onSuccess: (d) => invalidate(d.workspace_id),
  });
}
export function useScopingAction() {
  const invalidate = useScopingInvalidate();
  return useMutation({
    mutationFn: ({ sid, action, body }) => api.post(`/scoping/${sid}/${action}`, body || {}).then((r) => r.data),
    onSuccess: (d) => invalidate(d.workspace_id),
  });
}
export function useDeleteScoping() {
  const invalidate = useScopingInvalidate();
  return useMutation({
    mutationFn: ({ sid }) => api.delete(`/scoping/${sid}`).then((r) => r.data),
    onSuccess: (_d, v) => invalidate(v.wid),
  });
}

// --- Global Intelligence — Admin Data Ops (Phase 6) ---
export function useIntelDataHealth() {
  return useQuery({ queryKey: ["intel-admin", "health"], queryFn: () => get("/intelligence/admin/data-health") });
}
export function useIntelSources() {
  return useQuery({ queryKey: ["intel-admin", "sources"], queryFn: () => get("/intelligence/admin/sources") });
}
export function useIntelDuplicates() {
  return useQuery({ queryKey: ["intel-admin", "duplicates"], queryFn: () => get("/intelligence/admin/duplicates", { limit: 100 }) });
}
export function useIntelEnrichmentQueue(page = 1, country = "") {
  return useQuery({
    queryKey: ["intel-admin", "enrichq", page, country],
    queryFn: () => get("/intelligence/admin/enrichment-queue", { page, page_size: 20, country: country || undefined }),
    placeholderData: keepPreviousData,
  });
}
export function useIntelBatches() {
  return useQuery({ queryKey: ["intel-admin", "batches"], queryFn: () => get("/intelligence/admin/batches") });
}
function useIntelAdminInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["intel-admin"] });
    qc.invalidateQueries({ queryKey: ["intel"] });
  };
}
export function useIntelMerge() {
  const invalidate = useIntelAdminInvalidate();
  return useMutation({
    mutationFn: (body) => api.post("/intelligence/admin/merge", body).then((r) => r.data),
    onSuccess: invalidate,
  });
}
export function useIntelReenrich() {
  const invalidate = useIntelAdminInvalidate();
  return useMutation({
    mutationFn: (ids) => api.post("/intelligence/admin/enrichment-queue/reenrich", { ids }).then((r) => r.data),
    onSuccess: invalidate,
  });
}
export function useIntelApproveBatch() {
  const invalidate = useIntelAdminInvalidate();
  return useMutation({
    mutationFn: (id) => api.post(`/intelligence/admin/batches/${id}/approve`).then((r) => r.data),
    onSuccess: invalidate,
  });
}
export function useIntelRollbackBatch() {
  const invalidate = useIntelAdminInvalidate();
  return useMutation({
    mutationFn: (id) => api.delete(`/intelligence/admin/batches/${id}`).then((r) => r.data),
    onSuccess: invalidate,
  });
}

// --- Client Posture & Value (Phase 7) ---
export function usePosture(wid) {
  return useQuery({ queryKey: ["posture", wid], queryFn: () => get(`/workspaces/${wid}/posture`), enabled: !!wid });
}
export function usePostureTrend(wid) {
  return useQuery({ queryKey: ["posture-trend", wid], queryFn: () => get(`/workspaces/${wid}/posture/trend`), enabled: !!wid });
}
export function useBoardBriefs(wid) {
  return useQuery({ queryKey: ["board-briefs", wid], queryFn: () => get(`/workspaces/${wid}/board-briefs`), enabled: !!wid });
}
export function useGenerateBoardBrief(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/workspaces/${wid}/board-briefs`).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["board-briefs", wid] }); },
  });
}
export function useTogglePostureShare(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled) => api.post(`/workspaces/${wid}/posture/share`, { enabled }).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["workspace", wid] }); },
  });
}


// --- Client Portal (Phase 11) ---
export function usePortalPosture(wid) {
  return useQuery({ queryKey: ["portal-posture", wid], queryFn: () => get(`/workspaces/${wid}/portal/posture`), enabled: !!wid });
}
export function usePortalScoping(wid) {
  return useQuery({ queryKey: ["portal-scoping", wid], queryFn: () => get(`/scoping/portal/${wid}`), enabled: !!wid });
}
export function useSavePortalScoping(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ responses, complete }) => api.post(`/scoping/portal/${wid}/save`, { responses, complete }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-scoping", wid] }),
  });
}
export function useWorkspaceSignatures(wid) {
  return useQuery({ queryKey: ["signatures", wid], queryFn: () => get(`/workspaces/${wid}/signatures`), enabled: !!wid });
}
export function useSignature(sid) {
  return useQuery({ queryKey: ["signature", sid], queryFn: () => get(`/signatures/${sid}`), enabled: !!sid });
}
export function useRequestSignature(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ output_id, message }) => api.post(`/workspaces/${wid}/signatures`, { output_id, message }).then((r) => r.data),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["signatures", wid] });
      qc.invalidateQueries({ queryKey: ["outputs"] });
      qc.invalidateQueries({ queryKey: ["output", v.output_id] });
    },
  });
}
export function useSignSignature(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sid, signer_name, consent }) => api.post(`/signatures/${sid}/sign`, { signer_name, consent }).then((r) => r.data),
    onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: ["signatures", wid] }); qc.invalidateQueries({ queryKey: ["signature", v.sid] }); },
  });
}
export function useDeclineSignature(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sid, reason }) => api.post(`/signatures/${sid}/decline`, { reason }).then((r) => r.data),
    onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: ["signatures", wid] }); qc.invalidateQueries({ queryKey: ["signature", v.sid] }); },
  });
}
export function useVoidSignature(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sid) => api.post(`/signatures/${sid}/void`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["signatures", wid] }),
  });
}


// --- Firm Command Center & Engagement Economics (Phase 12) ---
export function useFirmCommandCenter() {
  return useQuery({ queryKey: ["firm-command-center"], queryFn: () => get("/command-center/overview") });
}
export function useCommandCenterMeta() {
  return useQuery({ queryKey: ["command-center-meta"], queryFn: () => get("/command-center/meta"), staleTime: 5 * 60_000 });
}
export function useRadarDigest({ enabled = true } = {}) {
  return useQuery({ queryKey: ["radar-digest"], queryFn: () => get("/radar/digest"), staleTime: 10 * 60_000, enabled });
}
export function useRefreshRadarDigest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => get("/radar/digest?refresh=true"),
    onSuccess: (data) => qc.setQueryData(["radar-digest"], data),
  });
}
export function useTeam() {
  return useQuery({ queryKey: ["cc-team"], queryFn: () => get("/command-center/team") });
}
function useTeamMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cc-team"] });
      qc.invalidateQueries({ queryKey: ["command-center"] });
    },
  });
}
export function useAddMember() {
  return useTeamMutation((body) => api.post("/command-center/team", body).then((r) => r.data));
}
export function useUpdateMember() {
  return useTeamMutation(({ mid, ...body }) => api.patch(`/command-center/team/${mid}`, body).then((r) => r.data));
}
export function useDeleteMember() {
  return useTeamMutation((mid) => api.delete(`/command-center/team/${mid}`).then((r) => r.data));
}
export function useAllocations(wid) {
  return useQuery({ queryKey: ["cc-allocations", wid], queryFn: () => get(`/command-center/workspaces/${wid}/allocations`), enabled: !!wid });
}
export function useAddAllocation(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/command-center/workspaces/${wid}/allocations`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cc-allocations", wid] });
      qc.invalidateQueries({ queryKey: ["cc-team"] });
      qc.invalidateQueries({ queryKey: ["command-center"] });
    },
  });
}
export function useDeleteAllocation(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aid) => api.delete(`/command-center/allocations/${aid}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cc-allocations", wid] });
      qc.invalidateQueries({ queryKey: ["cc-team"] });
      qc.invalidateQueries({ queryKey: ["command-center"] });
    },
  });
}

// --- Autonomous Assurance (Flagship — self-driving GRC) ---
export function useAutonomousOverview() {
  return useQuery({ queryKey: ["autonomous", "overview"], queryFn: () => get("/autonomous/overview"), refetchInterval: 60000 });
}
export function useAutonomousRemediations(status = "drafted") {
  return useQuery({ queryKey: ["autonomous", "remediations", status], queryFn: () => get("/autonomous/remediations", { status }) });
}
export function useAutonomousAdvisories() {
  return useQuery({ queryKey: ["autonomous", "advisories"], queryFn: () => get("/autonomous/advisories") });
}
export function useAutonomousWorkspaces() {
  return useQuery({ queryKey: ["autonomous", "workspaces"], queryFn: () => get("/autonomous/workspaces") });
}
function useAutonomousMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["autonomous"] }); },
  });
}
export function useRunSweep() {
  return useAutonomousMutation(() => api.post("/autonomous/sweep").then((r) => r.data));
}
export function useAcceptRemediation() {
  return useAutonomousMutation((rid) => api.post(`/autonomous/remediations/${rid}/accept`).then((r) => r.data));
}
export function useDismissRemediation() {
  return useAutonomousMutation((rid) => api.post(`/autonomous/remediations/${rid}/dismiss`).then((r) => r.data));
}
export function useToggleAutonomy() {
  return useAutonomousMutation(({ wid, ...body }) => api.post(`/autonomous/workspaces/${wid}/toggle`, body).then((r) => r.data));
}

// --- Meeting / Interview Copilot ---
export function useMeetings(workspaceId) {
  return useQuery({
    queryKey: ["meetings", "list", workspaceId || "all"],
    queryFn: () => get("/meetings", workspaceId ? { workspace_id: workspaceId } : undefined),
  });
}
export function useAnalyzeMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/meetings/analyze", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meetings"] }),
  });
}
export function useCreateMeetingTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/meetings/${id}/create-tasks`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meetings"] }),
  });
}
export function useDeleteMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/meetings/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meetings"] }),
  });
}

// --- Policy & Document Analyzer ---
export function usePolicyAnalyses(workspaceId) {
  return useQuery({
    queryKey: ["policy-analyses", "list", workspaceId || "all"],
    queryFn: () => get("/policy-analyzer", workspaceId ? { workspace_id: workspaceId } : undefined),
  });
}
export function usePolicyMeta() {
  return useQuery({ queryKey: ["policy-analyses", "meta"], queryFn: () => get("/policy-analyzer/meta") });
}
export function usePolicyAnalysis(id) {
  return useQuery({
    queryKey: ["policy-analyses", "detail", id],
    queryFn: () => get(`/policy-analyzer/${id}`),
    enabled: !!id,
  });
}
export function useAnalyzePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/policy-analyzer/analyze", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policy-analyses"] }),
  });
}
export function useCreatePolicyTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/policy-analyzer/${id}/create-tasks`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policy-analyses"] }),
  });
}
export function useDeletePolicyAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/policy-analyzer/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policy-analyses"] }),
  });
}

// --- Predictive Forecasting ---
export function useForecast(wid) {
  return useQuery({ queryKey: ["forecast", wid], queryFn: () => get(`/forecast/${wid}`), enabled: !!wid });
}
export function useSimulateForecast(wid) {
  return useMutation({ mutationFn: (body) => api.post(`/forecast/${wid}/simulate`, body).then((r) => r.data) });
}



// --- AI Trust & Defensibility (Phase 10) ---
export function useOutputTrust(id) {
  return useQuery({ queryKey: ["trust", id], queryFn: () => get(`/outputs/${id}/trust`), enabled: !!id });
}
export function useAnalyzeTrust(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/outputs/${id}/trust/analyze`).then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(["trust", id], data);
      qc.invalidateQueries({ queryKey: ["output", id] });
    },
  });
}
export function useTrustEval() {
  return useQuery({ queryKey: ["trust-eval"], queryFn: () => get(`/trust/eval`) });
}
export function useTrustTrends() {
  return useQuery({ queryKey: ["trust-trends"], queryFn: () => get(`/trust/trends`), staleTime: 5 * 60_000 });
}
export function useRunTrustEval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limit = 6) => api.post(`/trust/eval/run`, null, { params: { limit } }).then((r) => r.data),
    onSuccess: (data) => qc.setQueryData(["trust-eval"], data),
  });
}

// --- Phase 4.3 AI Observability + Phase 4.2 Golden-dataset eval ---
export function useTrustObservability() {
  return useQuery({ queryKey: ["trust-observability"], queryFn: () => get(`/trust/observability`) });
}
export function useGoldenEval() {
  return useQuery({
    queryKey: ["golden-eval"],
    queryFn: () => get(`/trust/golden-eval`),
    refetchInterval: (query) => (query.state.data?.running ? 5000 : false),
  });
}
export function useRunGoldenEval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/trust/golden-eval/run`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["golden-eval"] }),
  });
}

// --- Sector Benchmarking (Phase 9 — firm-internal, anonymized) ---
export function useSectorBenchmarks(minCohort = 3) {
  return useQuery({
    queryKey: ["benchmarks", "sectors", minCohort],
    queryFn: () => get(`/benchmarks/sectors?min_cohort=${minCohort}`),
  });
}
export function useWorkspaceBenchmark(wid, minCohort = 3) {
  return useQuery({
    queryKey: ["benchmark", wid, minCohort],
    queryFn: () => get(`/workspaces/${wid}/benchmark?min_cohort=${minCohort}`),
    enabled: !!wid,
  });
}

// --- Continuous Assurance (Phase 8 — multi-connector: M365 / AWS / Azure / GCP) ---
export function useAssurance(wid) {
  return useQuery({ queryKey: ["assurance", wid], queryFn: () => get(`/workspaces/${wid}/assurance`), enabled: !!wid });
}
function invalidateAssurance(qc, wid) {
  qc.invalidateQueries({ queryKey: ["assurance", wid] });
  qc.invalidateQueries({ queryKey: ["workspace", wid] });
  qc.invalidateQueries({ queryKey: ["evidence", wid] });
  qc.invalidateQueries({ queryKey: ["findings", wid] });
  qc.invalidateQueries({ queryKey: ["grc-dashboard", wid] });
  qc.invalidateQueries({ queryKey: ["grc-health", wid] });
  qc.invalidateQueries({ queryKey: ["posture", wid] });
}
export function useConnectAssurance(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, ...body }) =>
      api.post(`/workspaces/${wid}/assurance/${provider}/connect`, body).then((r) => r.data),
    onSuccess: () => invalidateAssurance(qc, wid),
  });
}
export function useDisconnectAssurance(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider) => api.delete(`/workspaces/${wid}/assurance/${provider}/connect`).then((r) => r.data),
    onSuccess: () => invalidateAssurance(qc, wid),
  });
}
export function useRunAssuranceSync(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, scenario = "baseline" }) =>
      api.post(`/workspaces/${wid}/assurance/${provider}/sync`, null, { params: { scenario } }).then((r) => r.data),
    onSuccess: () => invalidateAssurance(qc, wid),
  });
}
export function useConfirmDrift(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ driftId, ...body }) =>
      api.post(`/workspaces/${wid}/assurance/drifts/${driftId}/confirm`, body).then((r) => r.data),
    onSuccess: () => invalidateAssurance(qc, wid),
  });
}
export function useDismissDrift(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (driftId) => api.post(`/workspaces/${wid}/assurance/drifts/${driftId}/dismiss`).then((r) => r.data),
    onSuccess: () => invalidateAssurance(qc, wid),
  });
}


export function useTopProspects({ country, limit, sector, enabled = true }) {
  return useQuery({
    queryKey: ["intel-top-prospects", country, limit, sector],
    queryFn: () => get("/intelligence/clients/top-prospects", { country, limit, ...(sector ? { sector } : {}) }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useBulkConvertIntel() {
  const invalidate = useIntelInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/intelligence/clients/bulk-convert", body),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["intel-top-prospects"] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

export function useVerifyIntelClient() {
  const invalidate = useIntelInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/intelligence/clients/${id}/verify`, body).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useBulkVerifyIntel() {
  const invalidate = useIntelInvalidate();
  return useMutation({
    mutationFn: (body) => api.post("/intelligence/clients/bulk-verify", body).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useImportIntelCsv() {
  const invalidate = useIntelInvalidate();
  return useMutation({
    mutationFn: ({ file, country }) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post(`/intelligence/clients/import-csv?country=${encodeURIComponent(country || "Ghana")}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((r) => r.data);
    },
    onSuccess: invalidate,
  });
}

// Typeahead over the VERIFIED directory — used to prefill a new Client Workspace.
export function useIntelSuggest(q, country = "Ghana") {
  return useQuery({
    queryKey: ["intel-suggest", country, q],
    queryFn: () => api.get(`/intelligence/clients?country=${encodeURIComponent(country)}`
      + `&verification_status=verified&q=${encodeURIComponent(q)}&page_size=8`).then((r) => r.data.items),
    enabled: (q || "").trim().length >= 2,
    staleTime: 30000,
  });
}


// ---------------------------------------------------------------------------
// Integration Framework
// ---------------------------------------------------------------------------
export function useIntegrationMeta() {
  return useQuery({ queryKey: ["integration-meta"], queryFn: () => get("/integrations/meta"), staleTime: 5 * 60_000 });
}

export function useIntegrations(filters = {}) {
  return useQuery({
    queryKey: ["integrations", filters],
    queryFn: () => get("/integrations", clean(filters)),
  });
}

function useIntegrationInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["integrations"] });
    qc.invalidateQueries({ queryKey: ["integration-logs"] });
    qc.invalidateQueries({ queryKey: ["workspace-integrations"] });
  };
}

export function useWorkspaceLookup() {
  return useQuery({ queryKey: ["workspace-lookup"], queryFn: () => get("/workspaces/lookup"), staleTime: 5 * 60_000 });
}

export function useWorkspaceIntegrations(workspaceId) {
  return useQuery({
    queryKey: ["workspace-integrations", workspaceId],
    queryFn: () => get(`/integrations/workspace/${workspaceId}`),
    enabled: !!workspaceId,
  });
}

export function useCreateIntegration() {
  const invalidate = useIntegrationInvalidate();
  return useMutation({ mutationFn: (body) => api.post("/integrations", body).then((r) => r.data), onSuccess: invalidate });
}

export function useUpdateIntegration() {
  const invalidate = useIntegrationInvalidate();
  return useMutation({ mutationFn: ({ id, body }) => api.patch(`/integrations/${id}`, body).then((r) => r.data), onSuccess: invalidate });
}

export function useDeleteIntegration() {
  const invalidate = useIntegrationInvalidate();
  return useMutation({ mutationFn: (id) => api.delete(`/integrations/${id}`).then((r) => r.data), onSuccess: invalidate });
}

export function useTestIntegration() {
  const invalidate = useIntegrationInvalidate();
  return useMutation({ mutationFn: (id) => api.post(`/integrations/${id}/test`).then((r) => r.data), onSuccess: invalidate });
}

export function useSyncIntegration() {
  const invalidate = useIntegrationInvalidate();
  return useMutation({ mutationFn: (id) => api.post(`/integrations/${id}/sync`).then((r) => r.data), onSuccess: invalidate });
}

export function useIntegrationLogs(filters = {}) {
  return useQuery({
    queryKey: ["integration-logs", filters],
    queryFn: () => get("/integrations/logs/list", clean(filters)),
  });
}

export function useWebhooks() {
  return useQuery({ queryKey: ["webhooks"], queryFn: () => get("/integrations/webhooks/list") });
}

function useWebhookInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["webhooks"] });
}

export function useCreateWebhook() {
  const invalidate = useWebhookInvalidate();
  return useMutation({ mutationFn: (body) => api.post("/integrations/webhooks", body).then((r) => r.data), onSuccess: invalidate });
}

export function useUpdateWebhook() {
  const invalidate = useWebhookInvalidate();
  return useMutation({ mutationFn: ({ id, body }) => api.patch(`/integrations/webhooks/${id}`, body).then((r) => r.data), onSuccess: invalidate });
}

export function useDeleteWebhook() {
  const invalidate = useWebhookInvalidate();
  return useMutation({ mutationFn: (id) => api.delete(`/integrations/webhooks/${id}`).then((r) => r.data), onSuccess: invalidate });
}

export function useTestWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/integrations/webhooks/${id}/test`).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["webhooks"] }); qc.invalidateQueries({ queryKey: ["integration-logs"] }); },
  });
}

export function useAIProviders() {
  return useQuery({ queryKey: ["ai-providers"], queryFn: () => get("/integrations/ai/providers") });
}

export function useSaveAIProviders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.put("/integrations/ai/providers", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-providers"] }),
  });
}


// ---- Pricing: rate card + estimates (BOE) ----
export function useRateCard() {
  return useQuery({ queryKey: ["rate-card"], queryFn: () => get("/pricing/rate-card"), staleTime: 60_000 });
}

export function useUpdateRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.put("/pricing/rate-card", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rate-card"] }),
  });
}

export function useEstimates({ page = 1, pageSize = 25, filters } = {}) {
  return useQuery({
    queryKey: ["estimates", page, pageSize, filters],
    queryFn: () => get("/pricing/estimates", { page, page_size: pageSize, ...clean(filters || {}) }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/pricing/estimates", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimates"] }),
  });
}

export function useUpdateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/pricing/estimates/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimates"] }),
  });
}

export function useDeleteEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/pricing/estimates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimates"] }),
  });
}

export function useApplyEstimateEconomics() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/pricing/estimates/${id}/apply-economics`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["firm-command-center"] });
      qc.invalidateQueries({ queryKey: ["workspace"] });
    },
  });
}

// ---- Master Document Assembly ----
export function useMasterDocs({ page = 1, pageSize = 100, filters } = {}) {
  return useQuery({
    queryKey: ["master-docs", page, pageSize, filters],
    queryFn: () => get("/master-documents", { page, page_size: pageSize, ...clean(filters || {}) }),
    placeholderData: keepPreviousData,
  });
}

export function useMasterDoc(id, enabled = true) {
  return useQuery({
    queryKey: ["master-doc", id],
    queryFn: () => get(`/master-documents/${id}`),
    enabled: !!id && enabled,
  });
}

export function useCreateMasterDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/master-documents", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["master-docs"] }),
  });
}

export function useUpdateMasterDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/master-documents/${id}`, body).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["master-docs"] });
      qc.invalidateQueries({ queryKey: ["master-doc", vars.id] });
    },
  });
}

export function useDeleteMasterDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/master-documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["master-docs"] }),
  });
}


// ---- Workspace membership (Hybrid tenancy) ----
export function useWorkspaceMembers(workspaceId) {
  return useQuery({
    queryKey: ["workspace-members", workspaceId],
    queryFn: () => get(`/workspaces/${workspaceId}/members`),
    enabled: !!workspaceId,
  });
}

export function useAddWorkspaceMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, user_id, role }) =>
      api.post(`/workspaces/${workspaceId}/members`, { user_id, role }).then((r) => r.data),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-members", v.workspaceId] }),
  });
}

export function useUpdateWorkspaceMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, userId, role }) =>
      api.patch(`/workspaces/${workspaceId}/members/${userId}`, { role }).then((r) => r.data),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-members", v.workspaceId] }),
  });
}

export function useRemoveWorkspaceMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, userId }) => api.delete(`/workspaces/${workspaceId}/members/${userId}`),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-members", v.workspaceId] }),
  });
}

export function useWorkspaceAudit(workspaceId) {
  return useQuery({
    queryKey: ["workspace-audit", workspaceId],
    queryFn: () => get(`/workspaces/${workspaceId}/audit`),
    enabled: !!workspaceId,
  });
}

// ---- Client-user invitations ----
export function useWorkspaceClients(workspaceId) {
  return useQuery({
    queryKey: ["workspace-clients", workspaceId],
    queryFn: () => get(`/workspaces/${workspaceId}/clients`),
    enabled: !!workspaceId,
  });
}

export function useInviteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, email, name }) =>
      api.post(`/workspaces/${workspaceId}/invites`, { email, name }).then((r) => r.data),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-clients", v.workspaceId] }),
  });
}

export function useResendClientInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, userId }) =>
      api.post(`/workspaces/${workspaceId}/clients/${userId}/resend`).then((r) => r.data),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-clients", v.workspaceId] }),
  });
}

export function useUpdateClientAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, userId, is_active }) =>
      api.patch(`/workspaces/${workspaceId}/clients/${userId}`, { is_active }).then((r) => r.data),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-clients", v.workspaceId] }),
  });
}

export function useRemoveClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, userId }) => api.delete(`/workspaces/${workspaceId}/clients/${userId}`),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-clients", v.workspaceId] }),
  });
}

// --- Self-serve portal invite links -----------------------------------------
export function useWorkspacePortalLinks(workspaceId, enabled = true) {
  return useQuery({
    queryKey: ["workspace-portal-links", workspaceId],
    queryFn: () => get(`/workspaces/${workspaceId}/portal-links`),
    enabled: !!workspaceId && enabled,
  });
}

export function useCreatePortalLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, domain_restriction, label, max_uses }) =>
      api.post(`/workspaces/${workspaceId}/portal-links`, { domain_restriction, label, max_uses: max_uses || 1 }).then((r) => r.data),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-portal-links", v.workspaceId] }),
  });
}

export function usePresets(assistantKey) {
  return useQuery({
    queryKey: ["presets", assistantKey || "all"],
    queryFn: () => get(`/presets${assistantKey ? `?assistant_key=${assistantKey}` : ""}`),
  });
}
export function useUsePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/presets/${id}/use`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presets"] }),
  });
}
export function useDeletePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/presets/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presets"] }),
  });
}
export function useNotificationPrefs() {
  return useQuery({ queryKey: ["notification-prefs"], queryFn: () => get("/notifications/prefs") });
}
export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs) => api.put("/notifications/prefs", { prefs }).then((r) => r.data),
    onSuccess: (data) => qc.setQueryData(["notification-prefs"], data),
  });
}
export function useUpsellSignals() {
  return useQuery({ queryKey: ["upsell-signals"], queryFn: () => get("/pipeline/upsell-signals"), staleTime: 5 * 60_000 });
}

export function useRotatePortalLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, linkId }) =>
      api.post(`/workspaces/${workspaceId}/portal-links/${linkId}/rotate`).then((r) => r.data),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-portal-links", v.workspaceId] }),
  });
}

export function useRevokePortalLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, linkId }) =>
      api.delete(`/workspaces/${workspaceId}/portal-links/${linkId}`).then((r) => r.data),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["workspace-portal-links", v.workspaceId] }),
  });
}



// ---------------------------------------------------------------------------
// DUCF control library (read-only reference data — the master control backbone)
// ---------------------------------------------------------------------------
export function useDucfStats() {
  return useQuery({ queryKey: ["ducf-stats"], queryFn: () => get("/ducf/stats"), staleTime: 5 * 60_000 });
}

export function useDucfQuestionsCoverage() {
  return useQuery({
    queryKey: ["ducf-questions-coverage"],
    queryFn: () => get("/ducf/questions/coverage"),
    refetchInterval: (query) => (query.state.data?.job?.status === "running" ? 4000 : false),
  });
}
export function useBackfillDucfQuestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/ducf/questions/backfill").then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ducf-questions-coverage"] }),
  });
}

export function useDucfDomains() {
  return useQuery({ queryKey: ["ducf-domains"], queryFn: () => get("/ducf/domains"), staleTime: 5 * 60_000 });
}

// Phase 3.3 — SME sign-off of the DUCF↔framework/Ghana crosswalk mappings.
export function useMappingSignoffSummary() {
  return useQuery({ queryKey: ["mapping-signoff-summary"], queryFn: () => get("/ducf/mappings/signoff-summary"), staleTime: 60_000 });
}
export function useSignoffMapping(controlId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ family, status, note = "" }) =>
      api.post(`/ducf/controls/${controlId}/mappings/signoff`, { family, status, note }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ducf-control-insights", controlId] });
      qc.invalidateQueries({ queryKey: ["mapping-signoff-summary"] });
    },
  });
}

export function useDucfFamilies() {
  return useQuery({ queryKey: ["ducf-families"], queryFn: () => get("/ducf/families"), staleTime: 5 * 60_000 });
}

export function useDucfControls({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["ducf-controls", page, pageSize, filters],
    queryFn: () => get("/ducf/controls", { page, page_size: pageSize, ...clean(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useDucfControl(id, enabled = true) {
  return useQuery({
    queryKey: ["ducf-control", id],
    queryFn: () => get(`/ducf/controls/${id}`),
    enabled: !!id && enabled,
  });
}

export function useDucfFrameworks({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["ducf-frameworks", page, pageSize, filters],
    queryFn: () => get("/ducf/frameworks", { page, page_size: pageSize, ...clean(filters) }),
    placeholderData: keepPreviousData,
  });
}

// Control Intelligence (overview · per-control insights · crosswalk · AI actions)
export function useDucfOverview() {
  return useQuery({ queryKey: ["ducf-overview"], queryFn: () => get("/ducf/overview"), staleTime: 60_000 });
}
export function useDucfControlInsights(id) {
  return useQuery({
    queryKey: ["ducf-control-insights", id],
    queryFn: () => get(`/ducf/controls/${id}/insights`),
    enabled: !!id,
  });
}
export function useDucfCrosswalk({ familyA, familyB, domain }) {
  return useQuery({
    queryKey: ["ducf-crosswalk", familyA, familyB, domain],
    queryFn: () => get("/ducf/crosswalk", clean({ family_a: familyA, family_b: familyB, domain })),
    enabled: !!familyA,
    placeholderData: keepPreviousData,
  });
}
export function useDucfControlAi(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/ducf/controls/${id}/ai`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ducf-control-insights", id] }),
  });
}

// Framework Requirements Explorer (260 frameworks × DUCF crosswalk)
export function useFrameworkStats() {
  return useQuery({ queryKey: ["fw-stats"], queryFn: () => get("/frameworks/stats"), staleTime: 300_000 });
}
export function useFrameworkList({ page, pageSize, q }) {
  return useQuery({
    queryKey: ["fw-list", page, pageSize, q],
    queryFn: () => get("/frameworks", clean({ page, page_size: pageSize, q })),
    placeholderData: keepPreviousData,
  });
}
export function useFrameworkDetail(id) {
  return useQuery({
    queryKey: ["fw-detail", id],
    queryFn: () => get(`/frameworks/${id}`),
    enabled: !!id,
  });
}
export function useWorkspaceReadiness(id) {
  return useQuery({
    queryKey: ["ws-readiness", id],
    queryFn: () => get(`/workspaces/${id}/readiness`),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Ghana regulatory mapping (Phase 2B — read-only reference data)
// ---------------------------------------------------------------------------
export function useRegulationsStats() {
  return useQuery({ queryKey: ["regulations-stats"], queryFn: () => get("/regulations/stats"), staleTime: 5 * 60_000 });
}

export function useRegulationsMeta() {
  return useQuery({ queryKey: ["regulations-meta"], queryFn: () => get("/regulations/meta"), staleTime: 5 * 60_000 });
}

export function useRegulations(sector) {
  return useQuery({
    queryKey: ["regulations", sector || "all"],
    queryFn: () => get("/regulations", sector ? { sector } : undefined),
    staleTime: 5 * 60_000,
  });
}

export function useRegulationRequirements({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["regulation-requirements", page, pageSize, filters],
    queryFn: () => get("/regulations/requirements", { page, page_size: pageSize, ...clean(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useAssessmentTemplate(regulationId, sector, enabled = true) {
  return useQuery({
    queryKey: ["assessment-template", regulationId, sector || "all"],
    queryFn: () => get(`/regulations/${regulationId}/assessment-template`, sector ? { sector } : undefined),
    enabled: !!regulationId && enabled,
  });
}


// ---------------------------------------------------------------------------
// Workspace-scoped GRC Assessment engine (Phase 2C)
// ---------------------------------------------------------------------------
export function useGrcMeta(wid) {
  return useQuery({ queryKey: ["grc-meta", wid], queryFn: () => get(`/workspaces/${wid}/grc/meta`),
    enabled: !!wid, staleTime: 5 * 60_000 });
}

export function useAssessments(wid) {
  return useQuery({ queryKey: ["assessments", wid], queryFn: () => get(`/workspaces/${wid}/assessments`), enabled: !!wid });
}

export function useAssessment(wid, aid, enabled = true) {
  return useQuery({ queryKey: ["assessment", wid, aid], queryFn: () => get(`/workspaces/${wid}/assessments/${aid}`),
    enabled: !!wid && !!aid && enabled });
}

export function useFindings(wid, filters = {}) {
  return useQuery({ queryKey: ["findings", wid, filters], queryFn: () => get(`/workspaces/${wid}/findings`, clean(filters)), enabled: !!wid });
}

export function useCreateAssessment(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/workspaces/${wid}/assessments`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assessments", wid] }),
  });
}

export function useUpdateAssessment(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ aid, ...body }) => api.patch(`/workspaces/${wid}/assessments/${aid}`, body).then((r) => r.data),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["assessments", wid] });
      qc.invalidateQueries({ queryKey: ["assessment", wid, v.aid] });
    },
  });
}

export function useRespondItem(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ aid, itemId, ...body }) => api.patch(`/workspaces/${wid}/assessments/${aid}/items/${itemId}`, body).then((r) => r.data),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["assessment", wid, v.aid] });
      qc.invalidateQueries({ queryKey: ["assessments", wid] });
    },
  });
}

export function useGenerateFindings(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aid) => api.post(`/workspaces/${wid}/assessments/${aid}/generate-findings`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["findings", wid] }),
  });
}

export function useAssessmentQuestions(wid, aid, enabled = true) {
  return useQuery({
    queryKey: ["assessment-questions", wid, aid],
    queryFn: () => get(`/workspaces/${wid}/assessments/${aid}/questions`),
    enabled: !!wid && !!aid && enabled,
  });
}

export function useGenerateItemQuestions(wid, aid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId) => api.post(`/workspaces/${wid}/assessments/${aid}/items/${itemId}/questions/generate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assessment-questions", wid, aid] }),
  });
}

export function useGenerateAllQuestions(wid, aid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/workspaces/${wid}/assessments/${aid}/questions/generate-all`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assessment-questions", wid, aid] }),
  });
}

export function useAiDetermineItem(wid, aid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId) => api.post(`/workspaces/${wid}/assessments/${aid}/items/${itemId}/ai-determine`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assessment", wid, aid] }),
  });
}
export function useAiDetermineAll(wid, aid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/workspaces/${wid}/assessments/${aid}/ai-determine-all`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assessment", wid, aid] }),
  });
}

// --- Audit requests (auditor ↔ client conversation) ----------------------------------------
export function useAuditRequestMeta(wid) {
  return useQuery({ queryKey: ["audit-request-meta", wid], queryFn: () => get(`/workspaces/${wid}/audit-requests/meta`), enabled: !!wid });
}
export function useAuditRequests(wid) {
  return useQuery({ queryKey: ["audit-requests", wid], queryFn: () => get(`/workspaces/${wid}/audit-requests`), enabled: !!wid });
}
export function useAuditRequest(wid, rid, enabled = true) {
  return useQuery({
    queryKey: ["audit-request", wid, rid],
    queryFn: () => get(`/workspaces/${wid}/audit-requests/${rid}`),
    enabled: !!wid && !!rid && enabled,
  });
}
function auditReqInvalidate(qc, wid, rid) {
  qc.invalidateQueries({ queryKey: ["audit-requests", wid] });
  if (rid) qc.invalidateQueries({ queryKey: ["audit-request", wid, rid] });
}
export function useCreateAuditRequest(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/workspaces/${wid}/audit-requests`, body).then((r) => r.data),
    onSuccess: () => auditReqInvalidate(qc, wid),
  });
}
export function usePostAuditMessage(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rid, ...body }) => api.post(`/workspaces/${wid}/audit-requests/${rid}/messages`, body).then((r) => r.data),
    onSuccess: (_d, v) => auditReqInvalidate(qc, wid, v.rid),
  });
}
export function useAcceptAuditRequest(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rid) => api.post(`/workspaces/${wid}/audit-requests/${rid}/accept`).then((r) => r.data),
    onSuccess: (_d, rid) => auditReqInvalidate(qc, wid, rid),
  });
}
export function useReopenAuditRequest(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rid) => api.post(`/workspaces/${wid}/audit-requests/${rid}/reopen`).then((r) => r.data),
    onSuccess: (_d, rid) => auditReqInvalidate(qc, wid, rid),
  });
}
export function useDeleteAuditRequest(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rid) => api.delete(`/workspaces/${wid}/audit-requests/${rid}`).then((r) => r.data),
    onSuccess: () => auditReqInvalidate(qc, wid),
  });
}

export function useDeleteAssessment(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aid) => api.delete(`/workspaces/${wid}/assessments/${aid}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assessments", wid] });
      qc.invalidateQueries({ queryKey: ["findings", wid] });
    },
  });
}

export function useUpdateFinding(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fid, ...body }) => api.patch(`/workspaces/${wid}/findings/${fid}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["findings", wid] }),
  });
}

export function useCreateFinding(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/workspaces/${wid}/findings`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["findings", wid] }),
  });
}

export function useDeleteFinding(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fid) => api.delete(`/workspaces/${wid}/findings/${fid}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["findings", wid] }),
  });
}


// ---------------------------------------------------------------------------
// GRC Phase 2C-2 / 2C-3: Risks, Tasks, Evidence, Policies, Audits, Dashboard
// ---------------------------------------------------------------------------
export function useGrcRecordsMeta(wid) {
  return useQuery({ queryKey: ["grc-records-meta", wid], queryFn: () => get(`/workspaces/${wid}/grc/records-meta`),
    enabled: !!wid, staleTime: 5 * 60_000 });
}

export function useGrcDashboard(wid) {
  return useQuery({ queryKey: ["grc-dashboard", wid], queryFn: () => get(`/workspaces/${wid}/grc/dashboard`), enabled: !!wid });
}
export function useGrcHealth(wid) {
  return useQuery({ queryKey: ["grc-health", wid], queryFn: () => get(`/workspaces/${wid}/grc/health`), enabled: !!wid, staleTime: 60_000 });
}

// Generic CRUD hook factory for a workspace-scoped GRC collection.
function grcList(key, path) {
  return (wid, filters = {}) => useQuery({
    queryKey: [key, wid, filters],
    queryFn: () => get(`/workspaces/${wid}/${path}`, clean(filters)),
    enabled: !!wid,
  });
}
function grcCreate(key, path) {
  return (wid) => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (body) => api.post(`/workspaces/${wid}/${path}`, body).then((r) => r.data),
      onSuccess: () => { qc.invalidateQueries({ queryKey: [key, wid] }); qc.invalidateQueries({ queryKey: ["grc-dashboard", wid] }); qc.invalidateQueries({ queryKey: ["grc-health", wid] }); },
    });
  };
}
function grcUpdate(key, path) {
  return (wid) => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, ...body }) => api.patch(`/workspaces/${wid}/${path}/${id}`, body).then((r) => r.data),
      onSuccess: () => { qc.invalidateQueries({ queryKey: [key, wid] }); qc.invalidateQueries({ queryKey: ["grc-dashboard", wid] }); qc.invalidateQueries({ queryKey: ["grc-health", wid] }); },
    });
  };
}
function grcDelete(key, path) {
  return (wid) => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id) => api.delete(`/workspaces/${wid}/${path}/${id}`).then((r) => r.data),
      onSuccess: () => { qc.invalidateQueries({ queryKey: [key, wid] }); qc.invalidateQueries({ queryKey: ["grc-dashboard", wid] }); qc.invalidateQueries({ queryKey: ["grc-health", wid] }); },
    });
  };
}

export const useRisks = grcList("risks", "risks");
export const useCreateRisk = grcCreate("risks", "risks");
export const useUpdateRisk = grcUpdate("risks", "risks");
export const useDeleteRisk = grcDelete("risks", "risks");
export function useRiskFromFinding(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fid) => api.post(`/workspaces/${wid}/risks/from-finding/${fid}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["risks", wid] });
      qc.invalidateQueries({ queryKey: ["findings", wid] });
      qc.invalidateQueries({ queryKey: ["grc-dashboard", wid] });
      qc.invalidateQueries({ queryKey: ["grc-health", wid] });
    },
  });
}

export const useTasks = grcList("tasks", "tasks");
export const useCreateTask = grcCreate("tasks", "tasks");
export const useUpdateTask = grcUpdate("tasks", "tasks");
export const useDeleteTask = grcDelete("tasks", "tasks");

export const useEvidence = grcList("evidence", "evidence");
export const useCreateEvidence = grcCreate("evidence", "evidence");
export const useUpdateEvidence = grcUpdate("evidence", "evidence");
export const useDeleteEvidence = grcDelete("evidence", "evidence");

export const usePolicies = grcList("policies", "policies");
export const useCreatePolicy = grcCreate("policies", "policies");
export const useUpdatePolicy = grcUpdate("policies", "policies");
export const useDeletePolicy = grcDelete("policies", "policies");

export const useAudits = grcList("audits", "audits");
export function useAudit(wid, aid, enabled = true) {
  return useQuery({ queryKey: ["audit", wid, aid], queryFn: () => get(`/workspaces/${wid}/audits/${aid}`),
    enabled: !!wid && !!aid && enabled });
}
export const useCreateAudit = grcCreate("audits", "audits");
export const useUpdateAudit = grcUpdate("audits", "audits");
export const useDeleteAudit = grcDelete("audits", "audits");


// Assessment creation wizard helpers
export function useGrcRegulationSuggestions(wid, sector, jurisdiction = "Ghana", enabled = true) {
  return useQuery({
    queryKey: ["grc-reg-suggestions", wid, jurisdiction, sector],
    queryFn: () => get(`/workspaces/${wid}/grc/regulation-suggestions`, clean({ jurisdiction, sector })),
    enabled: !!wid && enabled,
  });
}

export function useAssessmentPreview(wid, payload, enabled = true) {
  return useQuery({
    queryKey: ["assessment-preview", wid, payload],
    queryFn: () => api.post(`/workspaces/${wid}/assessments/preview`, payload).then((r) => r.data),
    enabled: !!wid && enabled && (payload?.scopes?.length > 0),
    placeholderData: keepPreviousData,
  });
}


// ---------------------------------------------------------------- Agent Framework
export function useAgentRegistry() {
  return useQuery({ queryKey: ["agent-registry"], queryFn: () => get(`/agents/registry`), staleTime: 300_000 });
}
export function useWorkspaceAgents(wid) {
  return useQuery({ queryKey: ["workspace-agents", wid], queryFn: () => get(`/workspaces/${wid}/agents`), enabled: !!wid });
}
export function useAgentRuns(wid) {
  return useQuery({ queryKey: ["agent-runs", wid], queryFn: () => get(`/workspaces/${wid}/agent-runs`), enabled: !!wid });
}
export function useAgentRun(wid, runId, enabled = true) {
  return useQuery({
    queryKey: ["agent-run", wid, runId],
    queryFn: () => get(`/workspaces/${wid}/agent-runs/${runId}`),
    enabled: !!wid && !!runId && enabled,
    refetchInterval: (q) => (q.state.data?.run?.status === "running" ? 4000 : false),
  });
}
export function useStartAgentRun(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, params }) => api.post(`/workspaces/${wid}/agents/${agentId}/run`, { trigger: "manual", params: params || {} }).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["workspace-agents", wid] }); qc.invalidateQueries({ queryKey: ["agent-runs", wid] }); },
  });
}
export function useAgentDrafts(wid, reviewStatus) {
  return useQuery({
    queryKey: ["agent-drafts", wid, reviewStatus || "all"],
    queryFn: () => get(`/workspaces/${wid}/agent-drafts`, clean({ review_status: reviewStatus })),
    enabled: !!wid,
  });
}
export function useReviewAgentDraft(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, ...body }) => api.post(`/workspaces/${wid}/agent-drafts/${draftId}/review`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent-drafts", wid] });
      qc.invalidateQueries({ queryKey: ["program", wid] });
      qc.invalidateQueries({ queryKey: ["understanding", wid] });
      qc.invalidateQueries({ queryKey: ["engagement-plan", wid] });
      qc.invalidateQueries({ queryKey: ["compliance-program", wid] });
      qc.invalidateQueries({ queryKey: ["client-success", wid] });
      qc.invalidateQueries({ queryKey: ["workspace-agents", wid] });
      qc.invalidateQueries({ queryKey: ["agent-activity"] });
      // Approving/converting drafts can create real GRC records:
      ["risks", "tasks", "policies", "grc-dashboard", "grc-health"].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k, wid] }));
    },
  });
}
export function useAgentEvents(wid) {
  return useQuery({ queryKey: ["agent-events", wid], queryFn: () => get(`/workspaces/${wid}/agent-events`), enabled: !!wid });
}
export function useInsight360(wid) {
  return useQuery({ queryKey: ["insight360", wid], queryFn: () => get(`/workspaces/${wid}/insight360`), enabled: !!wid });
}
export function useInsight360Act(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/workspaces/${wid}/insight360/act`, body).then((r) => r.data),
    onSuccess: () => {
      ["insight360", "agent-drafts", "tasks", "audit-requests", "scoping-ws", "grc-health", "grc-dashboard"]
        .forEach((k) => qc.invalidateQueries({ queryKey: [k, wid] }));
    },
  });
}
export function useSetInsight360Schedule(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cadence) => api.post(`/workspaces/${wid}/insight360/schedule`, { cadence }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["insight360", wid] }),
  });
}
export function useRunQualityReview(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId }) => api.post(`/workspaces/${wid}/agent-drafts/${draftId}/quality-review`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-drafts", wid] }),
  });
}
export function useProgram(wid) {
  return useQuery({ queryKey: ["program", wid], queryFn: () => get(`/workspaces/${wid}/program`), enabled: !!wid });
}
export function useClientUnderstanding(wid) {
  return useQuery({ queryKey: ["understanding", wid], queryFn: () => get(`/workspaces/${wid}/understanding`), enabled: !!wid });
}
export function useEngagementPlan(wid) {
  return useQuery({ queryKey: ["engagement-plan", wid], queryFn: () => get(`/workspaces/${wid}/engagement-plan`), enabled: !!wid });
}
export function useComplianceProgram(wid) {
  return useQuery({ queryKey: ["compliance-program", wid], queryFn: () => get(`/workspaces/${wid}/compliance-program`), enabled: !!wid });
}
export function useClientSuccessPlan(wid) {
  return useQuery({ queryKey: ["client-success", wid], queryFn: () => get(`/workspaces/${wid}/client-success`), enabled: !!wid });
}
export function usePatchProgram(wid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch) => api.patch(`/workspaces/${wid}/program`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["program", wid] }),
  });
}
export function useAgentActivity() {
  return useQuery({ queryKey: ["agent-activity"], queryFn: () => get(`/agents/activity`), staleTime: 60_000 });
}
export function useCommandCenter() {
  return useQuery({ queryKey: ["command-center"], queryFn: () => get(`/dashboard/command-center`), staleTime: 30_000 });
}
export function usePublishedAgentOutputs(wid) {
  return useQuery({ queryKey: ["published-agent-outputs", wid], queryFn: () => get(`/workspaces/${wid}/agent-outputs/published`), enabled: !!wid });
}

